import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { Member } from "src/redux/types/_customer.type";
import HealthReportForm from "./components/health-report-form";
import DietaryReportForm from "./components/dietary-report-form";
import BasicInfo from "./components/basic-info";
import BasicInfoForm from "./components/basic-info-form";
import { ArrowLeft } from "lucide-react";
import { useFetchMembers } from "./hooks/useFetchMembers";

const AddReport = () => {
  const { customerId, subId, name } = useParams();
  const navigate = useNavigate();

  const { data: members, isLoading } = useFetchMembers(Number(subId));

  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const onChange = (id: string) => {
    if (!id) return;

    const member = members.find((m: Member) => m.id === Number(id));

    setSelectedMember(member);
  };

  const updateSelected = (member: Member) => {
    setSelectedMember(member);
  };

  if (isLoading) {
    return (
      <div className="text-center w-[80%] mx-auto">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="px-4 relative">
      {/* header */}
      <header className="py-3 pl-4 pr-8 w-full rounded-lg h-14 bg-white/30 backdrop-blur-sm shadow-md sticky top-0 flex items-center">
        <button
          className="p-2 rounded-full text-sm  hover:bg-gray-200 border flex items-center justify-center duration-300 ease-in-out transition-all"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="size-4" />
        </button>
        <h1 className="text-center text-lg font-semibold w-full">
          Add Health Report for Member/s of {name}
        </h1>
      </header>

      {!selectedMember?.id && (
        <div className="p-3 rounded-xl bg-amber-50 max-w-xl mx-auto my-3">
          <p className="text-md font-semibold text-amber-600 text-center">
            Select a Member to Add Report
          </p>
        </div>
      )}

      <div className="max-w-lg pt-3 mx-auto">
        <Select onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select Member to Add Report" />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(members) &&
              members.map((member: Member) => (
                <SelectItem key={member.id} value={String(member.id)}>
                  {member.first_name} {member.last_name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {selectedMember?.id && (
        <div className="sticky top-0 justify-center bg-white py-2 rounded-xl min-w-3xl mx-auto my-3">
          <h3 className="capitalize text-lg font-semibold text-primary text-center">
            Adding Health Report for {selectedMember.first_name}{" "}
            {selectedMember.last_name}
          </h3>
        </div>
      )}

      {selectedMember?.id &&
        (selectedMember?.dob ? (
          <BasicInfo member={selectedMember} updateSelected={updateSelected} />
        ) : (
          <BasicInfoForm
            member={selectedMember}
            subscriptionId={selectedMember?.subscription_id}
            updateSelected={updateSelected}
          />
        ))}

      {selectedMember?.id && (
        <>
          {/* health report */}
          <HealthReportForm
            customerId={Number(customerId)}
            memberId={Number(selectedMember.id)}
          />

          {/* dietary report */}
          <DietaryReportForm
            customerId={Number(customerId)}
            memberId={Number(selectedMember.id)}
          />
        </>
      )}
    </div>
  );
};

export default AddReport;
